import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import Modal from '../modals/common/Modal';
import { toastService } from '../../services/toastService';

// Constants for timeout intervals (in milliseconds)
const LOGOUT_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIMEOUT = 10 * 60 * 1000; // 10 minutes (5 min warning before logout)

// For testing/verification (uncomment to test)
// const LOGOUT_TIMEOUT = 20 * 1000; // 20 seconds
// const WARNING_TIMEOUT = 10 * 1000; // 10 seconds

export default function SessionTimeoutHandler() {
    const { isAuthenticated, signOut } = useAuthContext();
    const [showWarning, setShowWarning] = useState(false);
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    const handleTimeoutLogout = useCallback(async () => {
        try {
            setShowWarning(false);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

            await signOut();
            toastService.info('You have been logged out due to inactivity.', 5000);
        } catch (error) {
            console.error('Error logging out due to inactivity:', error);
        }
    }, [signOut]);

    const resetTimers = useCallback(() => {
        if (!isAuthenticated) return;

        // Clear existing timers
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

        // Always hide warning when resetting timers (user is active)
        setShowWarning(false);

        lastActivityRef.current = Date.now();

        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
        }, WARNING_TIMEOUT);

        logoutTimerRef.current = setTimeout(() => {
            handleTimeoutLogout();
        }, LOGOUT_TIMEOUT);
    }, [isAuthenticated, handleTimeoutLogout]);

    const handleActivity = useCallback(() => {
        const now = Date.now();
        if (now - lastActivityRef.current > 1000) {
            resetTimers();
        }
    }, [resetTimers]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
            setShowWarning(false);
            return;
        }

        resetTimers();

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

        events.forEach(event => {
            window.addEventListener(event, handleActivity);
        });

        // Clean up
        return () => {
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

            events.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
        };
    }, [isAuthenticated, handleActivity, resetTimers]);

    const handleStayLoggedIn = () => {
        resetTimers();
        setShowWarning(false);
    };

    return (
        <Modal
            isOpen={showWarning}
            onClose={() => { }}
            className="session-timeout-modal"
        >
            <div className="modal-content" style={{ padding: '2rem', maxWidth: '400px', textAlign: 'center' }}>
                <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Session Timeout Warning</h3>
                <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                    You will be logged out in 5 minutes due to inactivity.
                </p>
                <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                    Please move your mouse or click the button below to stay logged in.
                </p>
                <button
                    onClick={handleStayLoggedIn}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: 'var(--accent-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Stay Logged In
                </button>
            </div>
        </Modal>
    );
}
